import { baseApi } from './base-api';
import { API_ENDPOINTS } from '../utils/constants';
import { getIdentityGeneration } from '../utils/identity-scope';

export interface Datastream {
  id: string;
  name: string;
  type: string;
  owner?: string;
  org_id?: string;
  status?: 'active' | 'deactivated' | 'archived';
  has_collected_data?: boolean;
  state?: string | null;
  settings?: Record<string, unknown> | null;
  search_priority?: number;
  json_target_url?: string | null;
  custom_schema?: Array<{ index: number; name: string; type: string }> | null;
}

interface GetDatastreamsArgs {
  uid?: string;
  org_id?: string;
  filters?: Record<string, string | number | boolean>;
}

export interface CreateDatastreamArgs {
  name?: string;
  type: string;
  status?: string;
  settings?: Record<string, any>;
  json_target_url?: string;
  custom_schema?: Datastream['custom_schema'];
  search_priority?: number;
}

interface ResetDatastreamArgs {
  stream_id: string;
  type: string;
}

interface FileIndexOutcome {
  id: string;
  process_queued: boolean;
  train_queued: boolean;
  already_queued: boolean;
  error?: string;
}

interface FileIndexResponse {
  type: string;
  response: {
    files: FileIndexOutcome[];
    total_count: number;
    queued_count: number;
    already_queued_count: number;
    failed_count: number;
  };
}

export interface DatastreamResponse {
  response: Datastream;
  type: string;
}

export interface DatastreamConfigurationUpdate {
  name?: string;
  settings?: Record<string, unknown> | null;
  search_priority?: number;
  json_target_url?: string | null;
  custom_schema?: Array<{ index: number; name: string; type: string }>;
}

export type DatastreamUpdate = DatastreamConfigurationUpdate | { status: 'active' | 'deactivated' | 'archived' };
export interface DatastreamUpdateResponse {
  type: 'success';
  response: boolean | Datastream;
}

interface StoredText {
  id: string;
  title: string;
  content: string;
  hide_from_search_results: boolean;
  always_include: boolean;
}

interface QueueStatusResponse {
  type: string;
  response: {
    status: 'queued' | 'started' | 'finished' | 'failed' | 'stopped' | 'canceled' | 'scheduled' | null;
    position: number | null;
  };
}
interface StoredTextResponse {
  response: StoredText;
  type: string;
}

interface StoredTextsResponse {
  response: StoredText[];
}

interface DatastreamsResponse {
  response: {
    list: Datastream[];
    total: number;
  };
  type: string;
}

export interface SearchField {
  key: string;
  label: string;
  type: 'NUMERIC' | 'TEXT' | 'TAG';
}

interface SearchFieldsResponse {
  response: SearchField[];
  type: string;
}

// New: the shape of each file you’re sending
export interface CompletedFile {
  id: string;
  path: string;
  url: string;
  metadata: {
    name: string;
    type: string;
    size: number;
  };
}

export interface UploadFilesArgs {
  datastream_id: string;
  files: File[]; // Use actual File objects from input type="file"
  indexAutomatically?: boolean;
}

export interface UpdateFilesArgs {
  datastream_id: string;
  files: CompletedFile[];
}

// New: what the server returns (adjust to your real API contract)
export interface UploadFilesResponse {
  response: {
    success: boolean;
    uploaded_count: number;
  };
  type: string;
}

// --- Kept as they were necessary for the 'getStreamFiles' fix and were missing ---
export interface GetStreamFilesArgs {
  datastream_id: string;
  page?: number;
  per_page?: number;
}

export interface StreamFile {
  id: string;
  metadata: {
    name: string;
    size?: number;
    type?: string;
  };
  exposed_file_url?: string;
}

export interface GetStreamFilesResponse {
  response: {
    list: StreamFile[];
    total: number;
  };
  type: string; // e.g., 'success'
}
// --- End of new interfaces (kept) ---

const datastreamsApi = baseApi.enhanceEndpoints({ addTagTypes: ['Datastreams', 'Datastream'] }).injectEndpoints({
  endpoints: (builder) => ({
    createDatastream: builder.mutation<DatastreamResponse, CreateDatastreamArgs>({
      query: ({ name, type, status, settings, json_target_url, custom_schema, search_priority }) => ({
        url: '/streams',
        method: 'POST',
        body: { name, type, status, settings, json_target_url, custom_schema, search_priority },
      }),
      invalidatesTags: ['Datastreams', 'Datastream'],
    }),
    getDatastreams: builder.query<DatastreamsResponse, GetDatastreamsArgs>({
      query: ({ uid, org_id, filters }) => {
        // Build the URL with query parameters
        const url = new URL('/v1/streams', API_ENDPOINTS.KARLA_FLASK_API);
        const params: Record<string, string> = {};

        if (uid) params['uid'] = uid;
        if (org_id) params['org_id'] = org_id;

        // Append parameters to the URL - using original assumption of filters being JSON string
        Object.entries(filters ?? {}).forEach(([key, value]) => url.searchParams.append(key, String(value)));

        // Append parameters to the URL
        Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]));

        return {
          url: url.toString(),
          method: 'GET', // Using GET to fetch data
        };
      },
      providesTags: ['Datastreams'],
    }),
    getDatastreamById: builder.query<DatastreamResponse, string>({
      query: (id) => {
        // Build the URL with query parameters
        const url = new URL(`/v1/streams/${id}`, API_ENDPOINTS.KARLA_FLASK_API);
        const params: Record<string, string> = {};

        // Append parameters to the URL
        Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]));

        return {
          url: url.toString(),
          method: 'GET', // Using GET to fetch data
        };
      },
      providesTags: (result, error, id) => [{ type: 'Datastream', id }],
    }),
    getSearchFields: builder.query<SearchFieldsResponse, string>({
      query: (modelId) => ({
        url: `models/${modelId}/action-flows/searchable-fields`,
        method: 'GET',
      }),
    }),
    updateDatastream: builder.mutation<DatastreamUpdateResponse, { id: string; updatedData: DatastreamUpdate }>({
      query: ({ id, updatedData }) => ({
        url: `streams/${id}`,
        method: 'PATCH',
        body: updatedData,
      }),
      onQueryStarted: async ({ id, updatedData }, { dispatch, queryFulfilled, getState }) => {
        const generation = getIdentityGeneration();
        const state = getState() as any;

        const cachedDatastream = datastreamsApi.endpoints.getDatastreamById.select(id)(state)?.data;
        const prevFilesArePublic = cachedDatastream?.response?.settings?.files_are_public;
        const nextFilesArePublic = 'settings' in updatedData ? updatedData.settings?.files_are_public : undefined;
        try {
          await queryFulfilled;
          if (generation !== getIdentityGeneration()) return;
          // Re-index files if their public state has changed
          if (typeof nextFilesArePublic === 'boolean' && prevFilesArePublic !== nextFilesArePublic) {
            dispatch(datastreamsApi.endpoints.indexAllFiles.initiate({ datastream_id: id }));
          }
        } catch {
          // Failed writes must not trigger file processing.
        }
      },
      invalidatesTags: (result, error, { id }) => (error ? [] : ['Datastreams', { type: 'Datastream', id }]),
    }),
    updateDatastreamsStatus: builder.mutation<void, { ids: string[]; status: 'active' | 'deactivated' | 'archived' }>({
      query: ({ ids, status }) => ({
        url: 'streams/status',
        method: 'PATCH',
        body: { ids, status },
      }),
      invalidatesTags: (result, error, { ids }) => ['Datastreams', ...ids.map((id) => ({ type: 'Datastream' as const, id }))],
    }),
    resetDatastream: builder.mutation<void, ResetDatastreamArgs>({
      query: ({ stream_id, type }) => ({
        url: `streams/${stream_id}/reset-settings`,
        method: 'PUT',
        body: { type },
      }),
      invalidatesTags: ['Datastreams', 'Datastream'],
    }),
    resetDatastreamData: builder.mutation<void, { stream_id: string }>({
      query: ({ stream_id }) => ({
        url: `streams/${stream_id}/reset-data`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, { stream_id }) => (error ? [] : ['Datastreams', { type: 'Datastream', id: stream_id }]),
    }),
    deleteDatastream: builder.mutation<void, string | undefined>({
      query: (id) => ({
        url: `streams/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => ['Datastreams', { type: 'Datastream', id }],
    }),
    deleteDatastreams: builder.mutation<void, any[]>({
      // Reverted to original any[]
      query: (ids) => ({
        url: 'streams',
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (result, error, ids) => ['Datastreams', ...ids.map((id) => ({ type: 'Datastream' as const, id }))],
    }),

    indexSelectedFiles: builder.mutation<FileIndexResponse, { datastream_id: string; files: CompletedFile[] }>({
      query: ({ datastream_id, files }) => ({
        url: `streams/${datastream_id}/files/index`,
        method: 'POST',
        body: { files },
      }),
      transformResponse: (response: any) => response,
      invalidatesTags: (result, error, args) => [{ type: 'StreamFiles', id: args.datastream_id }],
    }),

    processFiles: builder.mutation<void, { datastream_id: string; files: CompletedFile[] }>({
      query: ({ datastream_id, files }) => ({
        url: `streams/${datastream_id}/files/process`,
        method: 'POST',
        body: { files },
      }),
      transformResponse: (response: any) => response,
      invalidatesTags: (result, error, args) => [{ type: 'StreamFiles', id: args.datastream_id }],
    }),

    indexAllFiles: builder.mutation<FileIndexResponse, { datastream_id: string }>({
      query: ({ datastream_id }) => ({
        url: `streams/${datastream_id}/files/index`,
        method: 'POST',
      }),
      transformResponse: (response: any) => response,
      invalidatesTags: (result, error, args) => [{ type: 'StreamFiles', id: args.datastream_id }],
    }),

    uploadFilesToDatastream: builder.mutation<UploadFilesResponse, UploadFilesArgs>({
      query: ({ datastream_id, files, indexAutomatically }) => {
        const formData = new FormData();
        files.forEach((file: File) => {
          formData.append('files', file);
        });

        if (indexAutomatically !== undefined) {
          formData.append('indexAutomatically', String(indexAutomatically));
        }

        return {
          url: `streams/${datastream_id}/files`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: any) => response,
      invalidatesTags: (result, error, args) => [{ type: 'StreamFiles', id: args.datastream_id }],
    }),

    updateFilesOnDatastream: builder.mutation<UploadFilesResponse, UpdateFilesArgs>({
      query: ({ datastream_id, files }) => ({
        url: `streams/${datastream_id}/files`,
        method: 'PATCH',
        body: { files },
      }),
      transformResponse: (response: any) => response,
      invalidatesTags: (result, error, args) => [{ type: 'StreamFiles', id: args.datastream_id }],
    }),

    getStreamFiles: builder.query<GetStreamFilesResponse, GetStreamFilesArgs>({
      query: ({ datastream_id }) => ({
        url: `streams/${datastream_id}/files`,
        method: 'GET',
      }),
      // This was the fix for stream files, keeping it as it was needed.
      providesTags: (result, error, args) => [{ type: 'StreamFiles', id: args.datastream_id }],
    }),

    // --- START OF REVERTED STORED TEXT ENDPOINTS ---
    getStoredText: builder.query<StoredTextsResponse, string>({
      query: (stream_id) => {
        const url = new URL(`/v1/streams/${stream_id}/stored-text`, API_ENDPOINTS.KARLA_FLASK_API);
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: ['StoredText'],
    }),

    getStoredTextById: builder.query<StoredTextResponse, string>({
      // Reverted argument back to string
      query: (stream_id, text_id) => {
        // Original: `stream_id` was the first arg, `text_id` was second. This is unusual for RTK query `query` function (expects one arg).
        // Original: `query: (stream_id, text_id)` implies the query would be called with two arguments.
        // RTK Query `query` functions typically receive a single argument (or void).
        // I'm restoring it exactly as written, but note this pattern.
        const url = new URL(`/v1/streams/${stream_id}/stored-text/${text_id}`, API_ENDPOINTS.KARLA_FLASK_API);
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: (result, error, id) => [{ type: 'StoredText', id }],
    }),

    createStoredTextEntry: builder.mutation<StoredText, string>({
      // Reverted arguments
      query: (stream_id) => ({
        // Reverted query function to original (single `stream_id` argument)
        url: `streams/${stream_id}/stored-text`, // Reverted URL
        method: 'POST',
      }),
      transformResponse: (response: StoredTextResponse) => response.response,
    }),

    updateStoredText: builder.mutation<{ type: 'success'; response: string }, { stream_id: string; stored_texts: Array<StoredText> }>({
      // Reverted types
      query: ({ stream_id, stored_texts }) => ({
        url: `streams/${stream_id}/stored-texts`,
        method: 'PATCH',
        body: stored_texts,
      }),

      invalidatesTags: (result, error) => (error ? [] : ['Datastream', 'StoredText']),
    }),

    deleteFiles: builder.mutation<void, { datastream_id: string; file_ids: string[] }>({
      query: ({ datastream_id, file_ids }) => ({
        url: `streams/${datastream_id}/files`,
        method: 'DELETE',
        body: { file_ids },
      }),
      invalidatesTags: (result, error, { datastream_id }) => [{ type: 'StreamFiles', id: datastream_id }],
    }),

    addDatastreamToQueue: builder.mutation<void, { datastreams: string[]; full_recrawl?: boolean }>({
      query: ({ datastreams, full_recrawl }) => ({
        url: `/streams/collect`,
        method: 'POST',
        body: { datastreams, ...(full_recrawl ? { full_recrawl: true } : {}) },
      }),
    }),

    stopDatastream: builder.mutation<void, string>({
      query: (stream_id) => ({
        url: `streams/${stream_id}/stop`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, stream_id) => (error ? [] : ['Datastreams', { type: 'Datastream', id: stream_id }]),
    }),

    getDatastreamQueueStatus: builder.query<QueueStatusResponse, string>({
      query: (id) => ({
        url: `/streams/${id}/queue-status`,
        method: 'GET',
      }),
    }),

    deleteStoredText: builder.mutation<void, { stream_id: string; text_id: string }>({
      // Reverted arguments
      query: ({ stream_id, text_id }) => ({
        url: `streams/${stream_id}/stored-text/${text_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error) => (error ? [] : ['StoredText']),
    }),
    // --- END OF REVERTED STORED TEXT ENDPOINTS ---

    duplicateDatastream: builder.mutation<void, string | undefined>({
      query: (id) => ({
        url: `streams/${id}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Datastream', id }],
    }),

    uploadJsonSource: builder.mutation<{ type: string; response: { gcs_path: string; filename: string } }, { stream_id: string; file: File }>({
      query: ({ stream_id, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `streams/${stream_id}/json-source`,
          method: 'POST',
          body: formData,
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useDeleteFilesMutation,
  useDeleteStoredTextMutation,
  useDeleteDatastreamsMutation,
  useDeleteDatastreamMutation,
  useGetStoredTextByIdQuery,
  useGetStoredTextQuery,
  useGetDatastreamsQuery,
  useGetDatastreamByIdQuery,
  useGetSearchFieldsQuery,
  useGetStreamFilesQuery,
  useGetDatastreamQueueStatusQuery,
  useUpdateDatastreamMutation,
  useUpdateDatastreamsStatusMutation,
  useUpdateFilesOnDatastreamMutation,
  useUpdateStoredTextMutation,
  useCreateDatastreamMutation,
  useCreateStoredTextEntryMutation,
  useIndexSelectedFilesMutation,
  useIndexAllFilesMutation,
  useProcessFilesMutation,
  useAddDatastreamToQueueMutation,
  useStopDatastreamMutation,
  useDuplicateDatastreamMutation,
  useUploadFilesToDatastreamMutation,
  useResetDatastreamMutation,
  useResetDatastreamDataMutation,
  useUploadJsonSourceMutation,
} = datastreamsApi;

export { datastreamsApi };
